import { createTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { Paper, Button } from '@mui/material';

// Modern Blue, Black & White color palette
const modernColors = {
  primary: '#4caf50', // Green
  secondary: '#424242', // Dark gray (near black)
  background: '#ffffff', // White background
  surface: '#fafafa', // Light gray surface
  text: '#000000', // Black text
  textSecondary: '#424242', // Dark gray
  accent: '#66bb6a', // Light green accent
  error: '#4caf50', // Green instead of red
  warning: '#4caf50', // Green instead of orange
  success: '#4caf50', // Green
  info: '#4caf50' // Green
};

// Modern Green, Black & White theme
export const themeCustom = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: modernColors.primary,
      light: '#66bb6a',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: modernColors.secondary,
      light: '#616161',
      dark: '#212121',
      contrastText: '#ffffff',
    },
    background: {
      default: modernColors.background,
      paper: modernColors.surface,
    },
    text: {
      primary: modernColors.text,
      secondary: modernColors.textSecondary,
    },
    error: {
      main: modernColors.error,
    },
    warning: {
      main: modernColors.warning,
    },
    success: {
      main: modernColors.success,
    },
    info: {
      main: modernColors.info,
    },
  },
  typography: {
    fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '1rem',
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(63, 81, 181, 0.15)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${modernColors.primary} 0%, #66bb6a 100%)`,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, #388e3c 0%, ${modernColors.primary} 100%)`,
          },
        },
        outlined: {
          borderColor: modernColors.primary,
          color: modernColors.primary,
          '&:hover': {
            backgroundColor: modernColors.primary,
            color: '#ffffff',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: modernColors.surface,
          border: '1px solid #e0e0e0',
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: modernColors.surface,
          border: '1px solid #e0e0e0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 8,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: modernColors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: modernColors.primary,
            },
          },
          '& .MuiInputLabel-root': {
            color: modernColors.textSecondary,
          },
          '& .MuiInputBase-input': {
            color: modernColors.text,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: modernColors.primary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: modernColors.primary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(63, 81, 181, 0.1)',
          color: modernColors.primary,
          border: '1px solid rgba(63, 81, 181, 0.3)',
          fontWeight: 600,
        },
      },
    },
  },
});

// Modern Green, Black & White styled components
export const GradientPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 113, 235, 0.2)',
  borderRadius: 20,
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0071EB 0%, #00D474 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 48px rgba(0, 113, 235, 0.2)',
    borderColor: '#0071EB',
    '&::before': {
      opacity: 1,
    },
  },
}));

export const AnimatedButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${modernColors.primary} 0%, #66bb6a 100%)`,
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '16px 32px',
  borderRadius: 12,
  textTransform: 'none',
  boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: `linear-gradient(135deg, #388e3c 0%, ${modernColors.primary} 100%)`,
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 35px rgba(76, 175, 80, 0.4)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
}));

export const HeaderBar = styled('div')(({ theme }) => ({
  background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
  borderRadius: 20,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 32px rgba(0, 113, 235, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
    animation: 'shimmer 2s infinite',
  },
  '@keyframes shimmer': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(100%)',
    },
  },
}));

export const NetflixCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${modernColors.surface} 0%, rgba(255, 255, 255, 0.9) 100%)`,
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
    borderColor: modernColors.primary,
  },
}));

export const NetflixButton = styled(Button)(({ theme, variant = 'contained' }) => ({
  background: variant === 'contained' 
    ? `linear-gradient(135deg, ${modernColors.primary} 0%, #66bb6a 100%)`
    : 'transparent',
  color: variant === 'contained' ? '#ffffff' : modernColors.primary,
  fontWeight: 700,
  fontSize: '1rem',
  padding: '14px 28px',
  borderRadius: 12,
  textTransform: 'none',
  border: variant === 'outlined' ? `2px solid ${modernColors.primary}` : 'none',
  boxShadow: variant === 'contained' ? '0 8px 25px rgba(76, 175, 80, 0.3)' : 'none',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: variant === 'contained'
      ? `linear-gradient(135deg, #388e3c 0%, ${modernColors.primary} 100%)`
      : 'rgba(76, 175, 80, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 12px 35px rgba(76, 175, 80, 0.4)' : 'none',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
})); 